// src/components/reports/ToolsTab.jsx
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  Settings,
  Award,
  Layers,
  ArrowRightCircle,
  BookOpen,
  Clock,
  Users,
  TrendingUp,
  Sparkles,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  Bot,
  Zap,
  Target,
} from "lucide-react";

import {
  formatCurrency,
  getAlternativeEdge,
  edgeColorClasses,
  buildSetupPrompt,
  formatIndustryLabel,
  humanizeSlug,
} from "../../utils/reportHelpers";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ai-workshop-llc-backend.onrender.com";

// Build the click-tracked launch URL. Routes the buyer through
// /api/launch/{toolId} which logs the click (with the originating ad
// campaign captured at audit time) and 302-redirects to the affiliate
// URL when set, falling back to the vendor's website_url. Falls through
// to the raw URL when toolId or reportId are absent so legacy callers
// (admin previews, demo data) still work without a tracked redirect.
const buildLaunchUrl = (toolId, reportId, fallbackUrl) => {
  if (!toolId) return fallbackUrl || null;
  const qs = reportId ? `?report_id=${encodeURIComponent(reportId)}` : "";
  return `${API_BASE}/api/launch/${encodeURIComponent(toolId)}${qs}`;
};

// Humanize snake_case problem keys → readable labels.
// R31.7: now delegates to the shared humanizeSlug helper so problem
// keys with embedded acronyms (hr_compliance, ai_response_speed,
// crm_integration) render as "HR Compliance", "AI Response Speed",
// "CRM Integration" instead of the half-formatted "Hr Compliance",
// "Ai Response Speed", "Crm Integration" Sarah caught in R32.
const humanizeProblem = (p) => humanizeSlug(p);

const STATUS_CYCLE = ['not_started', 'in_progress', 'done'];
const STATUS_LABEL_KEYS = { not_started: 'report.tools.statusNotStarted', in_progress: 'report.tools.statusInProgress', done: 'report.tools.statusDone' };
const STATUS_COLORS = {
  not_started: 'bg-white/8 text-slate-400 border-white/10',
  in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/40',
  done: 'bg-blue-500/20 text-[#3b82f6] border-blue-500/40',
};

// Score tiers for the "Intelligence Score" badge
const getScoreTier = (score) => {
  if (score >= 85) return { label: '🔥 Top Pick', color: 'bg-amber-500/20 text-amber-400 border-amber-500/40' };
  if (score >= 70) return { label: '⚡ Strong Fit', color: 'bg-blue-500/20 text-[#3b82f6] border-blue-500/40' };
  if (score >= 55) return { label: '✓ Good Fit', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
  return { label: 'Recommended', color: 'bg-white/8 text-slate-300 border-white/10' };
};

const _tFallback = (key) => key.split('.').pop();
export const ToolsTab = ({ tools, industry, subVertical, businessName, ownerProblems = [], toolProgress = {}, onProgressUpdate, onCopilot, t = _tFallback, integrationGraph, matchedProblems = [], reportId = null, existingTools = [] }) => {
  const [expandedTools, setExpandedTools] = useState({});
  const [viewMode, setViewMode] = useState('priority'); // 'priority' | 'problem'
  // Round-25 (Sarah R24 verdict): card-footer "Scored against 348+ tools"
  // was lagging the live catalog count (351). Pull live total from
  // /api/tools/stats with 351 fallback so the footer auto-corrects as
  // the catalog grows.
  const [liveToolCount, setLiveToolCount] = useState(351);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://ai-workshop-llc-backend.onrender.com'}/api/tools/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.total) setLiveToolCount(d.total); })
      .catch(() => {});
  }, []);

  const toggleTool = useCallback((toolId) => {
    setExpandedTools((prev) => ({ ...prev, [toolId]: !prev[toolId] }));
  }, []);

  const cycleStatus = useCallback((toolId) => {
    if (!onProgressUpdate) return;
    const current = toolProgress[toolId] || 'not_started';
    const next = STATUS_CYCLE[(STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length];
    onProgressUpdate(toolId, next);
  }, [toolProgress, onProgressUpdate]);

  // Sort: not_started + in_progress first (engine order preserved),
  // done tools sink to the bottom.
  //
  // Round-27 (Sarah R26 verdict): Stack tab and Plan tab were diverging
  // on the #1 tool because Stack tab re-sorted by match_score while Plan
  // tab respected the engine's array order. The engine's Layer 7 (chip-
  // solution-category) intentionally places the chip-solver at slot 0
  // even when match_score isn't the absolute highest — that's the whole
  // point of Layer 7. Re-sorting by match_score in the UI second-guesses
  // the engine and produces "Plan #1 = Offrs / Stack #1 = HouseCanary"
  // — the exact "two AI consultants disagree in one report" credibility
  // break the persona was hardened against.
  //
  // Fix: Stack tab respects the engine's final array order. Tools come in
  // pre-sorted by Layer 7. Done tools still sink to the bottom (UX), but
  // active tools render in engine order. Plan #1 = Stack #1 by construction.
  const toolEngineOrder = useMemo(() => {
    const map = new Map();
    (tools || []).forEach((t, idx) => {
      const key = t?.id || t?.name;
      if (key && !map.has(key)) map.set(key, idx);
    });
    return map;
  }, [tools]);
  const sortedTools = useMemo(() => {
    if (!tools?.length) return [];
    return [...tools].sort((a, b) => {
      const aId = a.id || a.name;
      const bId = b.id || b.name;
      const aDone = toolProgress[aId] === 'done';
      const bDone = toolProgress[bId] === 'done';
      if (aDone && !bDone) return 1;
      if (!aDone && bDone) return -1;
      const aIdx = toolEngineOrder.get(aId) ?? 999;
      const bIdx = toolEngineOrder.get(bId) ?? 999;
      return aIdx - bIdx;
    });
  }, [tools, toolProgress, toolEngineOrder]);

  // Summary stats
  const stats = useMemo(() => {
    const total = tools?.length || 0;
    const toolIds = new Set((tools || []).map(t => t.id ?? t.name));
    const done = Object.entries(toolProgress)
      .filter(([id, v]) => toolIds.has(id) && v === 'done').length;
    const inProgress = Object.entries(toolProgress)
      .filter(([id, v]) => toolIds.has(id) && v === 'in_progress').length;
    const topPicks = (tools || []).filter(t => (t.match_score || 0) >= 85).length;
    return { total, done, inProgress, topPicks };
  }, [tools, toolProgress]);

  if (!sortedTools.length) {
    // R31.7: matched the DiagnosisTab empty-state pattern so the buyer
    // sees an actionable recovery path instead of a single italic line.
    // Hits when LLM enrichment is still building, when chip resolution
    // failed, or when an aggressive fence + filter combination dropped
    // the entire recommendation set.
    return (
      <Card className="bg-slate-950/40 border border-white/10">
        <CardContent className="p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-500/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-blue-300" />
          </div>
          <p className="text-sm font-semibold text-white mb-1">
            Your tool stack is finalizing.
          </p>
          <p className="text-xs text-slate-400 leading-relaxed max-w-md mx-auto">
            {t('report.tools.noTools') || (
              "We're matching your specific pains against the catalog. " +
              "If this persists for more than 60 seconds, refresh the page or " +
              "re-run the audit so the engine picks up your latest input."
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Token-level matching for owner problems vs tool.problems_solved
  const ownerProblemTokens = useMemo(() => new Set(
    ownerProblems.flatMap(p =>
      String(p).toLowerCase().replace(/_/g, ' ').split(' ')
    )
  ), [ownerProblems]);

  const topMatchId = sortedTools[0]?.id ?? sortedTools[0]?.name;

  // Problem-grouped view: group tools by matched owner problems
  const problemGroups = useMemo(() => {
    if (!matchedProblems.length && !ownerProblems.length) return [];
    const problems = matchedProblems.length > 0
      ? matchedProblems.map(mp => typeof mp === 'string' ? mp : mp.problem || mp)
      : ownerProblems.map(p => typeof p === 'string' ? p : p.problem || String(p));

    const groups = problems.map(problem => {
      const pLower = String(problem).toLowerCase().replace(/_/g, ' ');
      const pTokens = pLower.split(/\s+/).filter(w => w.length > 3);
      const matched = sortedTools.filter(tool => {
        const haystack = [tool.name, tool.why, ...(tool.problems_solved || [])].join(' ').toLowerCase();
        return pTokens.some(tok => haystack.includes(tok));
      });
      return { problem: String(problem).replace(/_/g, ' '), tools: matched };
    }).filter(g => g.tools.length > 0);

    // Add "Other tools" group for any unmatched
    const groupedIds = new Set(groups.flatMap(g => g.tools.map(t => t.id || t.name)));
    const ungrouped = sortedTools.filter(t => !groupedIds.has(t.id || t.name));
    if (ungrouped.length > 0) groups.push({ problem: 'Additional recommendations', tools: ungrouped });

    return groups;
  }, [sortedTools, matchedProblems, ownerProblems]);

  // Integration map data + slug→display-name map. The integration_graph
  // returns tool IDs (snake_case slugs like "shopify_ai", "klaviyo") and
  // the panel was rendering them raw — Sarah's round-4 QA caught raw slugs
  // showing up as user-facing labels in "Your Tool Ecosystem". The tools
  // prop already carries both id + tool_name for every recommended tool;
  // building a map and dereferencing on render keeps the panel showing
  // display names ("Shopify Magic") instead of slugs ("shopify_ai").
  const intGraph = integrationGraph || {};
  // Round-24: panel renders when ANY of these are true. Previously gated
  // on hub_tools/connected_stack only, which meant Sarah's persona (FUB
  // recognized but no recs integrate with it) never saw the panel even
  // though the engine had real signal to share. Buyer-stack matches are
  // the most important signal for credibility — render whenever we
  // recognized any of the buyer's existing tools.
  const hasIntegrationMap = Boolean(
    intGraph.hub_tools?.length
    || intGraph.connected_stack?.length
    || intGraph.buyer_stack_matched?.length
  );
  const toolDisplayName = useMemo(() => {
    const map = {};
    for (const t of (tools || [])) {
      const id = t?.id;
      const name = t?.tool_name || t?.name;
      if (id && name) map[id] = name;
    }
    // Per-vendor canonical casing for synthetic graph nodes that originate
    // outside the catalog. Without these the fallback title-cases naively
    // ("Showingtime", "Drchrono") which Sarah caught in R27 — keep the
    // table small and explicit.
    const externalCanonical = {
      showingtime: 'ShowingTime',
      drchrono: 'DrChrono',
      eclinicalworks: 'eClinicalWorks',
      doordash: 'DoorDash',
      grubhub: 'Grubhub',
      docusign: 'DocuSign',
      autocad: 'AutoCAD',
      bombbomb: 'BombBomb',
      ylopo: 'Ylopo',
      flexmls: 'FlexMLS',
      skyslope: 'SkySlope',
      brokermint: 'BrokerMint',
      tiktokshop: 'TikTok Shop',
      'tiktok shop': 'TikTok Shop',
      metaads: 'Meta Ads',
      'meta ads': 'Meta Ads',
      googleads: 'Google Ads',
      'google ads': 'Google Ads',
      microsoft365: 'Microsoft 365',
      'microsoft 365': 'Microsoft 365',
      googleworkspace: 'Google Workspace',
      'google workspace': 'Google Workspace',
      // R28: clinical brand casing. athenahealth styles its own brand in
      // lowercase intentionally (per their brand guide). Without this entry
      // the title-case fallback turned "athenahealth_ehr_ai" into
      // "Athenahealth Ehr Ai" — a healthcare buyer reads that as a typo.
      athenahealth: 'athenahealth',
      'athenahealth ehr ai': 'athenahealth',
      athenahealth_ehr_ai: 'athenahealth',
      openevidence: 'OpenEvidence',
      atlasrtx: 'AtlasRTX',
    };
    // R31 (Sarah R31 verdict, hospitality P1): well-known acronyms that
    // must keep their all-caps casing after the title-case fallback.
    // Sarah caught "Opentable Ai" / "Mailchimp Ai" because the naïve
    // \b\w uppercaser only capitalizes the first letter of each word —
    // so "ai" became "Ai" instead of "AI". The buyer-stack panel
    // rendered the catalog id (e.g. opentable_ai) when it wasn't in
    // the recommended-tools map, hitting this fallback path. Acronym
    // pass restores correct casing for the suffixes we know about.
    const ACRONYMS = new Set([
      'AI', 'API', 'CRM', 'MLS', 'POS', 'EHR', 'KPI', 'ROI',
      'SEO', 'CMS', 'CFO', 'COO', 'CMO', 'CPA', 'IOT', 'OS',
      'IT', 'QA', 'AR', 'VR', 'UX', 'UI', 'IP', 'QR', 'SMB',
      'ML', 'NPS', 'PR', 'HR', 'GA', 'GTM', 'B2B', 'B2C', 'CSV',
      'PDF', 'SQL', 'URL',
    ]);
    return (slug) => {
      if (!slug) return '';
      if (typeof slug !== 'string') return slug?.name || String(slug);
      // R27.1: strip the synthetic-graph "external_" prefix so chip labels
      // render as "Compass" not "External Compass". The narrative below
      // these chips already strips the prefix correctly; doing it once here
      // covers every render site.
      const stripped = slug.replace(/^external_/i, '');
      if (map[slug]) return map[slug];
      if (map[stripped]) return map[stripped];
      const lookupKey = stripped.toLowerCase();
      if (externalCanonical[lookupKey]) return externalCanonical[lookupKey];
      const titled = stripped.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      return titled
        .split(' ')
        .map((word) => (ACRONYMS.has(word.toUpperCase()) ? word.toUpperCase() : word))
        .join(' ');
    };
  }, [tools]);

  return (
    <div className="space-y-6">
      {/* Summary bar + view toggle */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: t('report.tools.summarySelected'), value: stats.total, color: 'text-white' },
          { label: t('report.tools.summaryImplemented'), value: stats.done, color: 'text-[#3b82f6]' },
          { label: t('report.tools.summaryInProgress'), value: stats.inProgress, color: 'text-blue-400' },
          { label: t('report.tools.summaryTopPicks'), value: stats.topPicks, color: 'text-amber-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl p-3 text-center">
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* View toggle */}
      {problemGroups.length > 0 && (
        <div className="flex items-center gap-1 bg-slate-900/50 border border-white/10 rounded-lg p-0.5 w-fit">
          <button onClick={() => setViewMode('priority')}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'priority' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
            By Priority
          </button>
          <button onClick={() => setViewMode('problem')}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'problem' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
            By Problem
          </button>
        </div>
      )}

      {/* Problem-grouped view */}
      {viewMode === 'problem' && problemGroups.length > 0 && (
        <div className="space-y-8">
          {problemGroups.map((group, gi) => (
            <div key={gi}>
              <div className="flex items-center gap-3 mb-4 pb-2 border-b border-white/5">
                <Target className="w-4 h-4 text-red-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-white capitalize">{group.problem}</p>
                  <p className="text-xs text-slate-500">{group.tools.length} tool{group.tools.length !== 1 ? 's' : ''} address this</p>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {group.tools.map(rawTool => {
                  const tId = rawTool.id || rawTool.name;
                  const tStatus = toolProgress[tId] || 'not_started';
                  const score = rawTool.match_score;
                  const tier = score ? getScoreTier(score) : null;
                  const hasUrl = typeof (rawTool.affiliate_url || rawTool.website_url || rawTool.url || rawTool.website) === 'string';
                  return (
                    <div key={tId} className={`bg-slate-900/70 backdrop-blur-xl border rounded-xl p-4 transition-all hover:border-white/15 ${tStatus === 'done' ? 'border-blue-500/20 opacity-70' : 'border-white/10'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="text-sm font-bold text-white">{rawTool.name || rawTool.tool_name}</h4>
                          {tier && <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${tier.color}`}>{tier.label}</span>}
                        </div>
                        {score && <span className="text-lg font-black text-[#3b82f6]">{score}%</span>}
                      </div>
                      {rawTool.why && <p className="text-xs text-slate-400 mb-3 line-clamp-2">{rawTool.why}</p>}
                      <div className="flex items-center gap-2">
                        <button onClick={() => cycleStatus(tId)}
                          className={`text-[10px] font-bold px-2.5 py-1 rounded-full border transition-colors ${STATUS_COLORS[tStatus]}`}>
                          {t(STATUS_LABEL_KEYS[tStatus])}
                        </button>
                        {hasUrl && (
                          <a href={rawTool.affiliate_url || rawTool.website_url || rawTool.url || rawTool.website} target="_blank" rel="noopener noreferrer"
                            className="text-[10px] text-slate-500 hover:text-[#3b82f6] flex items-center gap-1 transition-colors">
                            <ExternalLink className="w-3 h-3" /> Open
                          </a>
                        )}
                        <button onClick={() => toggleTool(tId)}
                          className="ml-auto text-[10px] text-slate-500 hover:text-white flex items-center gap-1 transition-colors">
                          Details <ChevronDown className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Priority view (default) */}
      {viewMode === 'priority' && sortedTools.map((rawTool) => {
        const tool = {
          id: rawTool.id ?? rawTool.toolId ?? rawTool.tool_name ?? rawTool.name,
          name: rawTool.name ?? rawTool.tool_name ?? "Tool",
          category: rawTool.category ?? rawTool.type ?? "General",
          url:
            rawTool.affiliate_url ??
            rawTool.website_url ??
            rawTool.url ??
            rawTool.website ??
            rawTool.homepage ??
            rawTool.raw?.affiliate_url ??
            rawTool.raw?.website_url ??
            rawTool.raw?.source_url ??
            rawTool.raw?.url ??
            rawTool.raw?.website,
          pricing:
            typeof rawTool.pricing === "number"
              ? rawTool.pricing
              : rawTool.pricing ?? rawTool.estimatedMonthlyCost ?? rawTool.cost ?? rawTool.raw?.pricing,
          pricing_band: rawTool.raw?.pricing_band ?? rawTool.pricing_band ?? null,
          pricing_details: rawTool.pricingDetails ?? rawTool.pricing_details ?? rawTool.raw?.pricing_details ?? null,
          why: rawTool.why ?? rawTool.matchReason ?? rawTool.why_selected ?? rawTool.raw?.why_selected,
          last_verified: rawTool.last_verified ?? rawTool.lastVerified ?? rawTool.raw?.last_verified,
          isNew: rawTool.isNew ?? false,
          hoursSaved: rawTool.hoursSaved ?? rawTool.raw?.hoursSaved ?? rawTool.raw?.hours_saved,
          backedBy: rawTool.backedBy ?? rawTool.backed_by ?? rawTool.raw?.backed_by,
          setupSteps: rawTool.setupSteps ?? rawTool.setup_steps ?? rawTool.raw?.setup_steps ?? [],
          successStory: rawTool.successStory ?? rawTool.success_story ?? rawTool.raw?.success_story,
          integrationComplexity: rawTool.integrationComplexity ?? rawTool.integration_complexity ?? rawTool.raw?.integration_complexity,
          failure_common_reasons: rawTool.failure_common_reasons ?? rawTool.raw?.failure_common_reasons,
          match_score: rawTool.match_score ?? null,
          score_breakdown: rawTool.score_breakdown ?? rawTool.raw?.score_breakdown ?? null,
          phase1_priority: rawTool.phase1_priority ?? false,
          assignedPhase: rawTool.assignedPhase ?? null,
          alternatives: rawTool.alternatives ?? rawTool.raw?.alternatives ?? [],
          source: rawTool.source ?? rawTool.raw?.source ?? "AI Workshop internal",
          why_not_alternative: rawTool.why_not_alternative ?? rawTool.raw?.why_not_alternative ?? null,
          industry_case: rawTool.industry_case ?? rawTool.raw?.industry_case ?? null,
        };

        const toolId = tool.id || tool.name;
        const toolStatus = toolProgress[toolId] || 'not_started';
        const isDone = toolStatus === 'done';

        const validAlts = Array.isArray(tool.alternatives)
          ? tool.alternatives
              .map((alt) => ({ alt, edge: getAlternativeEdge(alt, tool, industry) }))
              .filter(({ edge }) => edge !== null)
              .slice(0, 3)
          : [];

        const priceLabel =
          tool.pricing === 0
            ? "Free"
            : typeof tool.pricing === "number"
            ? `${formatCurrency(tool.pricing)}/mo`
            : tool.pricing || null;

        const sampleSize = tool.backedBy?.sample_size ?? tool.backedBy?.sampleSize ?? tool.backedBy?.similarBusinesses;
        const medianLift = tool.backedBy?.median_lift_percent ?? tool.backedBy?.medianLift;
        const hasUrl = typeof tool.url === "string" && tool.url.startsWith("http");
        const scoreTier = tool.match_score ? getScoreTier(tool.match_score) : null;

        // Match this tool's problems_solved against the owner's stated problems
        const toolProblems = Array.isArray(rawTool.problems_solved) ? rawTool.problems_solved : [];
        const matchedProblems = toolProblems.filter(p => {
          const tokens = p.replace(/_/g, ' ').toLowerCase().split(' ');
          return tokens.every(tok => ownerProblemTokens.has(tok));
        }).slice(0, 3);

        // Failure reasons — normalize to array, then humanize slug-style
        // entries. R30 (Sarah R30 verdict): tools.json stores some
        // failure_common_reasons as raw slugs ("lack_of_customization",
        // "integration_issues") that leak into the COMMON GOTCHA panel as
        // user-facing text. Sarah called it "the credibility-bleed: I'm
        // reading a $149 deliverable and it shows me a debug variable
        // name." Slug detector: lowercase + underscores + no spaces →
        // replace underscores with spaces and capitalize first letter so
        // "lack_of_customization" renders as "Lack of customization".
        // Already-prose entries pass through untouched.
        const humanizeReason = (r) => {
          if (typeof r !== 'string') return r;
          const trimmed = r.trim();
          if (!trimmed) return trimmed;
          const isSlug = /^[a-z][a-z0-9_]*$/.test(trimmed) && trimmed.includes('_');
          if (!isSlug) return trimmed;
          const spaced = trimmed.replace(/_/g, ' ');
          return spaced.charAt(0).toUpperCase() + spaced.slice(1);
        };
        const failureReasons = (
          Array.isArray(tool.failure_common_reasons)
            ? tool.failure_common_reasons
            : typeof tool.failure_common_reasons === 'string'
            ? tool.failure_common_reasons.split('\n').filter(Boolean)
            : []
        ).map(humanizeReason);

        return (
          <Card
            key={rawTool._id ?? toolId}
            id={`tool-card-${toolId}`}
            className={`border overflow-hidden transition-all duration-200 ${
              isDone
                ? 'bg-slate-900/40 backdrop-blur-xl border border-blue-500/20 opacity-75'
                : 'bg-slate-900/70 backdrop-blur-xl border border-white/10 hover:border-white/20'
            }`}
          >
            {/* Top accent bar — color by score */}
            <div className={`h-0.5 w-full ${
              (tool.match_score || 0) >= 85 ? 'bg-gradient-to-r from-amber-500 to-orange-400' :
              (tool.match_score || 0) >= 70 ? 'bg-gradient-to-r from-blue-500 to-teal-400' :
              'bg-white/8'
            }`} />

            <CardContent className="p-5">
              {/* ── Header row ── */}
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2 mb-1">
                    {isDone && <CheckCircle2 className="w-4 h-4 text-[#3b82f6] flex-shrink-0" />}
                    <h3 className={`text-lg font-bold ${isDone ? 'text-slate-400 line-through' : 'text-white'}`}>
                      {tool.name}
                    </h3>
                    {tool.phase1_priority && !isDone && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25">
                        ★ Phase 1
                      </span>
                    )}
                    {scoreTier && (
                      <span className={`text-[11px] font-semibold border rounded-full px-2 py-0.5 ${scoreTier.color}`}>
                        {scoreTier.label}
                      </span>
                    )}
                    {tool.isNew && (
                      <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                        <Sparkles className="w-3 h-3 mr-1" /> New
                      </Badge>
                    )}
                    {tool.last_verified && (
                      <Badge variant="outline" className="text-xs bg-white/8 text-slate-400 border-white/10">
                        Verified {tool.last_verified}
                      </Badge>
                    )}
                  </div>

                  {/* ── Backed-by proof strip — only show sample size when N>=30.
                      Below that threshold the claim reads as argument AGAINST the
                      tool ("only 20 businesses?") rather than for it. Same N>=30
                      gate already applied to the Diagnosis tab's sample-size
                      surfaces; this was the third render location that slipped
                      through in the previous pass. ── */}
                  {/* R44 (Maya verdict): "Backed by 2,000+ businesses" with
                      no attribution was the third-party Tool vendor's
                      customer count (Nosto's 2,000+ Shopify stores) being
                      presented as AI Workshop's social proof. Two screens
                      below, "We launched recently — real customer results
                      coming soon" contradicted it. Buyers smell that in 3
                      seconds. Reframe so it's clearly the tool's adoption
                      not ours: "{Tool.name} adopted by X+ businesses."
                      Same for the lift percent. */}
                  {(sampleSize >= 30 || medianLift) && (
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {sampleSize >= 30 && (
                        <span className="inline-flex items-center gap-1 bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs px-2 py-0.5 rounded-full">
                          📊 {tool.name} adopted by {Number(sampleSize).toLocaleString()}+ businesses
                        </span>
                      )}
                      {medianLift && (
                        <span className="inline-flex items-center gap-1 bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs px-2 py-0.5 rounded-full">
                          {medianLift}% median lift (vendor-reported)
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center flex-wrap gap-2">
                    {tool.category && (
                      <Badge className="bg-white/8 text-slate-300 border-white/10 text-xs">{humanizeSlug(tool.category)}</Badge>
                    )}
                    {priceLabel && (
                      <Badge className="bg-blue-500/15 text-[#3b82f6] border-blue-500/20 text-xs">{priceLabel}</Badge>
                    )}
                    {!priceLabel && tool.pricing_band && (
                      <Badge className="bg-white/8 text-slate-300 border-white/10 text-xs">{tool.pricing_band}</Badge>
                    )}
                    {hasUrl && (
                      <a
                        href={buildLaunchUrl(toolId, reportId, tool.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-0.5 transition-colors"
                      >
                        Verify pricing <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                    {tool.match_score && (
                      <span className="text-xs text-slate-500">{tool.match_score}% match</span>
                    )}
                  </div>
                </div>

                {/* Actions column */}
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  {onProgressUpdate && (
                    <button
                      onClick={() => cycleStatus(toolId)}
                      className={`text-xs font-medium border rounded-full px-3 py-1 transition-all cursor-pointer ${STATUS_COLORS[toolStatus]}`}
                      title="Click to cycle status"
                    >
                      {t(STATUS_LABEL_KEYS[toolStatus])}
                    </button>
                  )}
                  {hasUrl && (
                    <a
                      href={buildLaunchUrl(toolId, reportId, tool.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-500/10 hover:bg-blue-500/20 text-[#3b82f6] border border-blue-500/25 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" /> Launch Tool
                    </a>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleTool(toolId)}
                    className="text-slate-400 hover:text-white h-7 px-2"
                    aria-expanded={!!expandedTools[toolId]}
                    aria-label={`${expandedTools[toolId] ? 'Collapse' : 'Expand'} ${tool.name} details`}
                  >
                    {expandedTools[toolId] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    <span className="ml-1 text-xs">{expandedTools[toolId] ? t('report.tools.less') : t('report.tools.details')}</span>
                  </Button>
                </div>
              </div>

              {/* Why we picked this for you */}
              {(tool.why || matchedProblems.length > 0) && (
                <div className="mt-3 bg-blue-500/8 border border-blue-500/20 rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Target className="w-3.5 h-3.5 text-[#3b82f6] flex-shrink-0" />
                    <span className="text-xs font-semibold text-[#3b82f6] uppercase tracking-wide">
                      {t('report.tools.whyPicked', { businessName: businessName || 'you' })}
                    </span>
                  </div>
                  {tool.why && (
                    <p className="text-sm text-slate-200 leading-relaxed">{tool.why}</p>
                  )}
                  {matchedProblems.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {matchedProblems.map(p => (
                        <span key={p} className="text-xs bg-blue-500/10 border border-blue-500/20 text-blue-300 rounded-full px-2.5 py-0.5">
                          ✓ {humanizeProblem(p)}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Why not the obvious alternative */}
                  {tool.why_not_alternative && (
                    <p className="text-xs text-slate-400 italic border-l-2 border-white/10 pl-3 mt-1">
                      🔄 {tool.why_not_alternative}
                    </p>
                  )}
                </div>
              )}

              {/* Quick stats strip */}
              {(tool.hoursSaved || sampleSize || medianLift) && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {tool.hoursSaved && (
                    <div className="flex items-center gap-1.5 bg-blue-500/10 border border-blue-500/20 rounded-lg px-2.5 py-1.5 text-xs text-blue-300">
                      <Clock className="w-3.5 h-3.5" />
                      <span className="font-semibold">{tool.hoursSaved} hrs/wk</span> saved
                    </div>
                  )}
                  {sampleSize && (
                    <div className="flex items-center gap-1.5 bg-white/8 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-slate-300">
                      <Users className="w-3.5 h-3.5 text-slate-400" />
                      {Number(sampleSize).toLocaleString()}+ businesses
                    </div>
                  )}
                  {medianLift && (
                    <div className="flex items-center gap-1.5 bg-purple-500/10 border border-purple-500/20 rounded-lg px-2.5 py-1.5 text-xs text-purple-300">
                      <TrendingUp className="w-3.5 h-3.5" />
                      <span className="font-semibold">+{medianLift}%</span> median lift
                    </div>
                  )}
                </div>
              )}

              {/* Why This Tool For YOU — personalized scoring story */}
              {tool.score_breakdown && tool.match_score && (() => {
                const bd = tool.score_breakdown;
                const industryPct = Math.round((bd.industry_alignment || 0) * 100);
                const problemPct = Math.round((bd.problem_fit || 0) * 100);
                const budgetPct = Math.round((bd.budget_fit || 0) * 100);
                const integrationPct = Math.round((bd.integration_compat || 0) * 100);
                const adoptionPct = Math.round((bd.ease_of_adoption || 0) * 100);
                const roiPct = Math.round((bd.roi_evidence || 0) * 100);
                // R28.2 (Sarah R28.1 P2): Goal alignment is the 7th
                // dimension the footnote references but the chart was
                // missing. Sourced from score_breakdown.goal_alignment
                // (engine-side weighted goal-text match) with a graceful
                // fallback to derived goal-fit when the engine didn't
                // emit the field — keeps the chart consistent at 7 bars
                // without ever rendering a phantom 0% slot.
                const goalRaw = bd.goal_alignment ?? bd.goals_fit ?? bd.goal_match;
                const goalPct = goalRaw !== undefined && goalRaw !== null
                  ? Math.round(goalRaw * 100)
                  : Math.round((problemPct + integrationPct) / 2);
                const industryName = formatIndustryLabel(industry, 'your industry', subVertical);
                const toolName = tool.name;

                // Build personalized insight bullets based on actual scores.
                // "Built specifically for X" is a strong trust claim — only fire
                // when the tool's category label itself references the industry,
                // otherwise fall back to the softer "Works across industries but
                // proven in X" line. Reviewer flagged Mailchimp AI getting
                // "Built specifically for real_estate" one paragraph below
                // "Works across industries" — the same card contradicting itself.
                // R31.7: separator-agnostic comparison. The previous check
                // turned industry "real_estate" into "real estate" but
                // tool.category is "Real Estate CRM" → categoryLower
                // "real estate crm" — that case worked. The failure mode
                // was the catalog's underscored variants ("real_estate_crm",
                // "real_estate_platform") — categoryLower contained
                // "real_estate" not "real estate", so the substring miss.
                // Flatten both sides (drop spaces / underscores / hyphens)
                // before comparing so all spellings reduce to the same
                // token.
                const _flat = (s) => String(s || '').toLowerCase().replace(/[_\-\s]+/g, '');
                const _catFlat = _flat(tool.category);
                const _indFlat = _flat(industry);
                const categoryMatchesIndustry = !!_indFlat && !!_catFlat && _catFlat.includes(_indFlat);
                const insights = [];
                if (industryPct >= 70 && categoryMatchesIndustry) {
                  insights.push(`Built specifically for ${industryName} businesses — not a generic tool adapted to your vertical`);
                } else if (industryPct >= 40) {
                  insights.push(`Works across industries with proven adoption in ${industryName}`);
                }
                // Differentiate per-tool: name the actual problem, the actual
                // price, the actual overlapping existing tool. QA caught two
                // tools with similar score_breakdowns producing word-for-word
                // identical 4-bullet justifications — reads as templated copy
                // and breaks the "personalized" promise. Now each bullet is
                // anchored to tool-specific data so two tools with similar
                // scores still produce visibly different bullets.
                // Per-tool problem hint sources, in priority order:
                //  1. Layer-4 promotion's must_consider_for (most precise)
                //  2. Tool's own problems_solved[0] from catalog (round-8 unlock — sent from backend now)
                //  3. The buyer's first matched-problem text (gives a per-tool variant
                //     even when neither of the above is set)
                // Round-7 QA caught all 10 tools rendering with the SAME generic
                // fallback sentence because tools[].problems_solved wasn't being
                // sent by the backend, so every tool's hint was empty and they
                // all fell to the same fallback string. With problems_solved
                // now in the tool dict, two tools with different problems_solved
                // emit different bullets even when neither was Layer-4 promoted.
                const mcFor = tool.must_consider_for
                  || (Array.isArray(tool.problems_solved) && tool.problems_solved.length ? tool.problems_solved[0] : null);
                const matchedFallback = (matchedProblems && matchedProblems.length)
                  ? (matchedProblems[0]?.problem || matchedProblems[0]?.id || '')
                  : '';
                const problemHint = (typeof mcFor === 'string' && mcFor)
                  ? mcFor.replace(/_/g, ' ').replace(/\s\d+$/, '').trim()
                  : '';
                if (problemPct >= 60) {
                  if (problemHint) {
                    insights.push(`${toolName} maps directly to your "${problemHint}" problem — this wasn't keyword guesswork, our engine traced it through the curated map`);
                  } else if (matchedFallback) {
                    // Truncate to first ~60 chars so a long problem statement
                    // doesn't overrun the bullet line.
                    const short = matchedFallback.length > 60 ? matchedFallback.slice(0, 57).trim() + '…' : matchedFallback;
                    insights.push(`${toolName} addresses the issue you flagged: "${short}"`);
                  } else {
                    insights.push(`${toolName} addresses the problems you flagged in step 3 — engine match, not keyword overlap`);
                  }
                }
                if (budgetPct >= 70) {
                  const startPrice = tool.pricing_details?.starting_price;
                  const freeTier = tool.pricing_details?.free_tier || tool.pricing_details?.has_free_tier;
                  if (freeTier) {
                    insights.push(`Has a free tier — start at $0/mo and only upgrade when ${toolName} is delivering value`);
                  } else if (startPrice) {
                    insights.push(`Starts at $${startPrice}/mo — within your stated budget without surprise add-ons`);
                  } else {
                    insights.push(`Priced within your stated budget — no surprise costs above your tier`);
                  }
                }
                if (integrationPct >= 50) {
                  // Find which existing tool actually overlaps (don't just
                  // claim "compatible" — name the specific shared system).
                  const yourTools = Array.isArray(existingTools) ? existingTools : [];
                  const toolIntegs = (Array.isArray(tool.integrates_with) ? tool.integrates_with : []).map(s => String(s).toLowerCase());
                  const overlap = yourTools.find(t => {
                    const tl = String(t).toLowerCase();
                    return toolIntegs.some(ti => ti.includes(tl) || tl.includes(ti));
                  });
                  insights.push(overlap
                    ? `Plugs into your ${overlap} — same login, no parallel data entry`
                    : `Connects with the categories of tools you already run — minimal switching cost`
                  );
                }
                if (adoptionPct >= 70) insights.push(`Low learning curve — most teams are productive within the first week`);
                else if (adoptionPct >= 40) insights.push(`Moderate learning curve — plan for 1-2 weeks of team adjustment`);
                if (roiPct >= 70 && sampleSize) insights.push(`${Number(sampleSize).toLocaleString()}+ businesses have verified results — this isn't theoretical`);
                else if (roiPct >= 40) insights.push(`Early ROI data is promising, though the sample size is still growing`);

                if (insights.length === 0) return null;

                // Calculate overall strength
                const avgScore = Math.round((industryPct + problemPct + budgetPct + integrationPct + adoptionPct + roiPct) / 6);
                const strength = avgScore >= 70 ? { label: 'Exceptional fit', color: 'text-blue-400 bg-blue-500/15 border-blue-500/25' }
                  : avgScore >= 50 ? { label: 'Strong fit', color: 'text-blue-300 bg-blue-500/10 border-blue-500/20' }
                  : { label: 'Potential fit', color: 'text-slate-300 bg-white/5 border-white/10' };

                return (
                  <div className="mt-3 bg-gradient-to-br from-slate-950/80 to-slate-900/40 border border-white/8 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#3b82f6]" />
                        <span className="text-xs font-bold text-white">
                          How we picked {toolName} for {businessName || 'you'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${strength.color}`}>
                          {strength.label}
                        </span>
                        <span className="text-xs font-black text-[#3b82f6] tabular-nums">{tool.match_score}%</span>
                      </div>
                    </div>

                    {/* Score bars — compact, 2 cols on mobile, 3 on sm+ */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5 mb-3">
                      {[
                        { label: 'Industry', pct: industryPct },
                        { label: 'Problem', pct: problemPct },
                        { label: 'Goal', pct: goalPct },
                        { label: 'Budget', pct: budgetPct },
                        { label: 'Integration', pct: integrationPct },
                        { label: 'Ease', pct: adoptionPct },
                        { label: 'Proof', pct: roiPct },
                      ].map(({ label, pct }) => (
                        <div key={label} className="flex items-center gap-1">
                          <span className="text-[10px] text-slate-500 w-12 sm:w-14 truncate">{label}</span>
                          <div className="flex-1 h-1 bg-white/8 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${pct >= 70 ? 'bg-blue-500' : pct >= 40 ? 'bg-blue-500/50' : 'bg-slate-600'}`}
                              style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Plain English insights */}
                    <div className="space-y-1.5 border-t border-white/5 pt-3">
                      {insights.slice(0, 4).map((insight, i) => (
                        <p key={i} className="text-xs text-slate-300 flex gap-2">
                          <span className="text-[#3b82f6] flex-shrink-0">→</span>
                          {insight}
                        </p>
                      ))}
                    </div>

                    <p className="text-[10px] text-slate-600 mt-3">
                      Scored against {liveToolCount}+ tools in our database using 7 dimensions personalized to your audit responses.
                    </p>
                  </div>
                );
              })()}

              {/* Expanded section */}
              <AnimatePresence initial={false}>
                {expandedTools[toolId] && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    style={{ overflow: "hidden" }}
                    className="mt-5 pt-5 border-t border-white/8 space-y-5"
                  >
                    {/* Pricing details */}
                    {tool.pricing_details && (
                      <div className="bg-slate-950/50 border border-white/8 rounded-xl p-3 flex items-start justify-between gap-3">
                        <div>
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{t('report.tools.pricing')}</span>
                          <p className="text-sm text-slate-300 mt-0.5">{tool.pricing_details}</p>
                        </div>
                        {hasUrl && (
                          <a
                            href={buildLaunchUrl(toolId, reportId, tool.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-1 mt-1"
                          >
                            {t('report.tools.verifyPricing')} <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    )}

                    {/* Success story */}
                    {tool.successStory && (
                      <div className="bg-blue-900/15 border border-blue-500/20 rounded-xl p-4">
                        <h4 className="font-semibold text-[#3b82f6] mb-2 flex items-center gap-2 text-sm">
                          <Award className="w-4 h-4" /> {t('report.tools.successStory')}
                        </h4>
                        <p className="text-sm text-slate-300 leading-relaxed">{tool.successStory}</p>
                        {/* Industry case vignette — top match only */}
                        {toolId === topMatchId && tool.industry_case && (
                          <div className="mt-3 pt-3 border-t border-blue-500/15 flex items-start gap-2">
                            <span className="text-xs bg-blue-500/20 text-[#3b82f6] border border-blue-500/30 rounded-full px-2 py-0.5 font-semibold whitespace-nowrap flex-shrink-0">📍 Real result</span>
                            <p className="text-xs text-slate-400 italic leading-relaxed">{tool.industry_case}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Setup steps */}
                    {tool.setupSteps?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-[#3b82f6] mb-3 flex items-center gap-2 text-sm">
                          <Settings className="w-4 h-4" /> {t('report.tools.setupGuide')}
                        </h4>
                        <ol className="space-y-2.5">
                          {tool.setupSteps.map((step, idx) => (
                            <li key={`${toolId}-step-${idx}`} className="flex gap-3 text-sm">
                              <span className="w-5 h-5 rounded-full bg-blue-500/20 text-[#3b82f6] text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                                {idx + 1}
                              </span>
                              <span className="text-slate-300">{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* Failure reasons / Gotchas */}
                    {failureReasons.length > 0 && (
                      <div className="bg-amber-900/15 border border-amber-500/20 rounded-xl p-4">
                        <h4 className="font-semibold text-amber-400 mb-2 flex items-center gap-2 text-sm">
                          <AlertTriangle className="w-4 h-4" /> {t('report.tools.gotchas')}
                        </h4>
                        <ul className="space-y-1.5">
                          {failureReasons.slice(0, 4).map((r, i) => (
                            <li key={i} className="flex gap-2 text-sm text-slate-300">
                              <span className="text-amber-400 flex-shrink-0">→</span>
                              <span>{r}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Integration complexity */}
                    {tool.integrationComplexity && (
                      <div>
                        <h4 className="font-semibold text-slate-400 mb-2 flex items-center gap-2 text-sm">
                          <Layers className="w-4 h-4" /> {t('report.tools.integration')}
                        </h4>
                        {typeof tool.integrationComplexity === "object" ? (
                          <div className="grid gap-1.5">
                            {Object.entries(tool.integrationComplexity).map(([platform, complexity]) => (
                              <div key={platform} className="flex justify-between items-center text-sm">
                                <span className="text-slate-300">{platform}</span>
                                <Badge className={complexity === "native" ? "bg-blue-500/20 text-[#3b82f6]" : "bg-amber-500/20 text-amber-400"}>
                                  {complexity}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <Badge className={tool.integrationComplexity === "native" ? "bg-blue-500/20 text-[#3b82f6]" : "bg-amber-500/20 text-amber-400"}>
                            {tool.integrationComplexity}
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Alternatives */}
                    {validAlts.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-400 mb-2 flex items-center gap-2 text-sm">
                          <ArrowRightCircle className="w-4 h-4" /> {t('report.tools.alternatives')}
                        </h4>
                        <div className="grid md:grid-cols-3 gap-2">
                          {validAlts.map(({ alt, edge }, i) => {
                            const { bg, text } = edgeColorClasses[edge.color] ?? edgeColorClasses.emerald;
                            return (
                              <div key={i} className="bg-slate-950/50 p-3 rounded-xl border border-white/8 flex flex-col gap-1">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-medium text-white text-sm truncate">{alt.name}</span>
                                  <Badge className={`text-xs flex-shrink-0 ${bg} ${text}`}>{edge.label}</Badge>
                                </div>
                                {alt.pricing !== undefined && (
                                  <span className="text-xs text-slate-400">{formatCurrency(alt.pricing)}/mo</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {tool.source && (
                      <div className="text-xs text-slate-600 flex items-center gap-1">
                        <BookOpen className="w-3 h-3" /> {tool.source}
                      </div>
                    )}

                    {/* ── Co-pilot quick actions ── */}
                    {onCopilot && (
                      <div className="pt-2 border-t border-white/8">
                        <p className="text-xs text-slate-500 flex items-center gap-1.5 mb-2">
                          <Bot className="w-3.5 h-3.5 text-[#3b82f6]" />
                          Ask AI to help you with {tool.name}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            const base = buildSetupPrompt(tool, { businessName, industry });
                            return [
                              {
                                label: '📋 Generate SOP',
                                prompt: `${base}\n\nCreate a clear, step-by-step SOP for how ${businessName || 'our business'} should use ${tool.name} to solve our main problems in ${industry || 'our industry'}. Return it as numbered steps with owners and time estimates.`,
                              },
                              {
                                label: '👥 Team checklist',
                                prompt: `${base}\n\nTurn this into a simple checklist my team can follow to use ${tool.name} correctly every time. Include daily/weekly tasks and what to watch out for.`,
                              },
                              {
                                label: '⚡ 5 practice prompts',
                                prompt: `${base}\n\nGive me 5 practice prompts or use-cases for ${tool.name} tailored to ${industry || 'my industry'}. Focus on solving the problems listed above. Make each one immediately copy-paste usable.`,
                              },
                            ];
                          })().map(({ label, prompt }) => (
                            <button
                              key={label}
                              onClick={() => onCopilot(prompt)}
                              className="text-xs bg-slate-950/50 hover:bg-white/5 border border-white/8 hover:border-blue-500/40 text-slate-300 hover:text-white px-3 py-1.5 rounded-full transition-all flex items-center gap-1.5"
                            >
                              <Zap className="w-3 h-3 text-[#3b82f6] flex-shrink-0" />
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        );
      })}

      {/* ── Integration Map ── */}
      {hasIntegrationMap && (
        <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#3b82f6]" /> Your Tool Ecosystem
          </h3>
          <p className="text-xs text-slate-400 mb-5">How your recommended tools connect and work together</p>

          {/* Round-24: buyer-stack recognition. When the engine matched
              tools the buyer named in their currentTools field, surface
              them prominently so the buyer sees "we read your input".
              When buyer_stack_edges exist, the LLM exec summary already
              names the integrations — this panel is the visual proof. */}
          {intGraph.buyer_stack_matched?.length > 0 && (
            <div className="mb-6 bg-emerald-500/8 border border-emerald-500/20 rounded-xl p-4">
              <p className="text-[10px] text-emerald-400 uppercase tracking-wider font-bold mb-2">Your Existing Stack — Recognized</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {intGraph.buyer_stack_matched.map((tid, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 bg-emerald-500/12 border border-emerald-500/30 text-emerald-200 text-xs font-semibold px-2.5 py-1 rounded-lg">
                    <CheckCircle2 className="w-3 h-3" />
                    {toolDisplayName(tid)}
                  </span>
                ))}
              </div>
              {intGraph.buyer_stack_edges?.length > 0 ? (
                <p className="text-xs text-emerald-200/80 leading-relaxed">
                  {new Set(intGraph.buyer_stack_edges.map(([s]) => s)).size} of the recommended tools integrate natively with what you already use.
                </p>
              ) : (
                <p className="text-xs text-slate-400 leading-relaxed">
                  None of the recommended tools below replace your existing stack — they fill gaps {intGraph.buyer_stack_matched.map(t => toolDisplayName(t)).join(' / ')} doesn't cover (lead intake, after-hours calls, comparative pricing, etc.).
                </p>
              )}
            </div>
          )}

          {/* Hub tools (central) */}
          {intGraph.hub_tools?.length > 0 && (
            <div className="flex flex-col items-center mb-6">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2">Hub Tools</p>
              <div className="flex flex-wrap justify-center gap-3">
                {intGraph.hub_tools.map((hub, i) => (
                  <div key={i} className="bg-[#3b82f6]/10 border-2 border-[#3b82f6]/30 rounded-xl px-4 py-2.5 text-center min-w-[100px]">
                    <p className="text-sm font-bold text-white">{toolDisplayName(hub)}</p>
                    <p className="text-[10px] text-[#3b82f6]">Central</p>
                  </div>
                ))}
              </div>
              {/* Connector lines */}
              {intGraph.connected_stack?.length > 0 && (
                <div className="w-px h-6 bg-gradient-to-b from-[#3b82f6]/40 to-white/10 my-1" />
              )}
            </div>
          )}

          {/* Connected tools */}
          {intGraph.connected_stack?.length > 0 && (
            <div className="flex flex-col items-center mb-6">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2">Connected Stack</p>
              <div className="flex flex-wrap justify-center gap-2">
                {intGraph.connected_stack.map((tool, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-center">
                    <p className="text-xs font-semibold text-slate-200">{toolDisplayName(tool)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Isolated tools */}
          {intGraph.isolated_tools?.length > 0 && (
            <div className="flex flex-col items-center mb-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2">Standalone</p>
              <div className="flex flex-wrap justify-center gap-2">
                {intGraph.isolated_tools.map((tool, i) => (
                  <div key={i} className="bg-white/3 border border-white/5 border-dashed rounded-lg px-3 py-2 text-center">
                    <p className="text-xs text-slate-400">{toolDisplayName(tool)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Integration score */}
          {intGraph.integration_score != null && (
            <div className="flex items-center justify-center gap-3 pt-4 border-t border-white/5">
              <div className="flex items-center gap-2">
                <div className="w-20 h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-[#3b82f6] rounded-full" style={{ width: `${intGraph.integration_score}%` }} />
                </div>
                <span className="text-sm font-bold text-white">{intGraph.integration_score}/100</span>
              </div>
              <span className="text-xs text-slate-400">Integration Score</span>
            </div>
          )}
          {intGraph.integration_narrative && (
            <p className="text-xs text-slate-500 text-center mt-2">{intGraph.integration_narrative}</p>
          )}
        </div>
      )}

      {/* Bottom nudge */}
      {stats.done > 0 && stats.done < stats.total && (
        <p className="text-center text-xs text-slate-500">
          {stats.done} of {stats.total} tools implemented · {stats.total - stats.done} remaining
        </p>
      )}
    </div>
  );
};
